import React, { useState } from 'react';
import {
  Box,
  Container,
  Header,
  SpaceBetween,
  FormField,
  Input,
  Button,
  Alert,
  Form,
  Textarea,
  Select
} from '@cloudscape-design/components';
import { useEventTesting, TestEventData } from '../hooks/useEventTesting';
import { useHelpPanel } from '../contexts/HelpContext';
import { InfoLink } from '../components/help/common';
import { EventTestingHelp } from '../components/help/EventTestingHelp';

interface EventTestingProps {
  addNotification: (notification: any) => void;
}

const EventTesting: React.FC<EventTestingProps> = ({ addNotification }) => {
  // Use our custom hook for event testing
  const { 
    loading, 
    result, 
    error,
    slackLoading,
    slackResult,
    triggerTestEvent,
    testSlackNotification
  } = useEventTesting();
  const { setContent } = useHelpPanel();
  
  // Store stable preview data values
  const [previewData, setPreviewData] = useState({
    event_id: Math.floor(Math.random() * 10000),
    unique_id: `test-${Date.now()}`,
    timestamp: new Date().toISOString()
  });
  
  // Form data with event type and required fields
  const [formData, setFormData] = useState({
    eventType: { label: 'New Deployment Customer', value: '1' },
    accountManagerEmail: '',
    accountManagerAlias: ''
  });

  const handleFormChange = (key: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  // Function to manually regenerate preview data IDs
  const regeneratePreviewIds = () => {
    setPreviewData({
      event_id: Math.floor(Math.random() * 10000),
      unique_id: `test-${Date.now()}`,
      timestamp: new Date().toISOString()
    });
  };

  // Prepare mock event data with real email/alias values
  const getMockEventData = (): TestEventData => {
    return {
      eventType: formData.eventType,
      customerName: 'Acme Corporation',
      accountId: '123456789012',
      solutionName: 'AWS Cloud Migration Factory',
      accountManagerEmail: formData.accountManagerEmail || 'account.manager@example.com',
      accountManagerAlias: formData.accountManagerAlias || 'account_manager',
      creationDate: previewData.timestamp.split('T')[0],
      isActive: { label: 'Yes', value: 'true' },
      daysActive: '30'
    };
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.accountManagerEmail) {
      addNotification({
        type: 'error',
        content: 'Account Manager Email is required for email testing',
        dismissible: true
      });
      return;
    }
    
    // Add notification that we're processing the event
    addNotification({
      type: 'info',
      content: 'Sending test email...',
      dismissible: true
    });
    
    // Call API through our hook with mock data plus real email
    const result = await triggerTestEvent(getMockEventData());
    
    // Show appropriate notification based on result
    if (result.success) {
      addNotification({
        type: 'success',
        content: 'Test email was successfully sent. Event ID: ' + result.eventId,
        dismissible: true,
        dismissTimeout: 8000
      });
    } else {
      addNotification({
        type: 'error',
        content: `Failed to send test email: ${result.message}`,
        dismissible: true
      });
    }
  };

  const handleTestSlackNotification = async () => {
    // Validate required fields
    if (!formData.accountManagerAlias) {
      addNotification({
        type: 'error',
        content: 'Account Manager Slack Alias is required for Slack notifications',
        dismissible: true
      });
      return;
    }
    
    // Add notification that we're processing the Slack test
    addNotification({
      type: 'info',
      content: 'Testing Slack notification...',
      dismissible: true
    });
    
    // Call API through our hook with mock data plus real Slack alias
    const result = await testSlackNotification(getMockEventData());
    
    // Show appropriate notification based on result
    if (result.success) {
      addNotification({
        type: 'success',
        content: 'Slack notification was successfully sent.',
        dismissible: true,
        dismissTimeout: 8000
      });
    } else {
      addNotification({
        type: 'error',
        content: `Failed to send Slack notification: ${result.message}`,
        dismissible: true
      });
    }
  };

  const handleClear = () => {
    setFormData({
      eventType: { label: 'New Deployment Customer', value: '1' },
      accountManagerEmail: '',
      accountManagerAlias: ''
    });
  };

  return (
    <Box padding="l">
      <SpaceBetween size="l">
        <Header
          variant="h1"
          info={<InfoLink onFollow={() => setContent(<EventTestingHelp />)} />}
        >
          Event Testing
        </Header>
        
        {/* Preview Event Data moved to the top */}
        <Container
          header={
            <Header 
              description="This JSON shows the event data that will be used for testing"
              actions={
                <Button
                  variant="normal"
                  onClick={regeneratePreviewIds}
                  iconName="refresh"
                >
                  Regenerate IDs
                </Button>
              }
            >
              Preview Test Event Data
            </Header>
          }
        >
          <Textarea
            value={JSON.stringify(
              {
                event_id: previewData.event_id,
                event_type: formData.eventType.value,
                event_name: formData.eventType.label,
                unique_id: previewData.unique_id,
                event_date: previewData.timestamp.split('T')[0],
                solution_response_name: "AWS Cloud Migration Factory",
                aws_account_id: "123456789012",
                customer_name: "Acme Corporation",
                account_manager_email: formData.accountManagerEmail || "account.manager@example.com",
                account_manager_alias: formData.accountManagerAlias || "account_manager",
                account_manager_full_name: "Account Manager",
                creation_time: previewData.timestamp,
                is_active_deployment: "Y",
                deployment_days_active: 30,
                response_id: `sol-${previewData.event_id}`
              },
              null,
              2
            )}
            readOnly
            rows={15}
            spellcheck={false}
          />
        </Container>

        {/* Simplified Form with only two fields */}
        <Container header={<Header>Test Configuration</Header>}>
          <Form>
            <SpaceBetween size="l">
              <FormField label="Event Type">
                <Select
                  selectedOption={formData.eventType}
                  onChange={({ detail }) =>
                    handleFormChange('eventType', detail.selectedOption)
                  }
                  options={[
                    { label: 'New Deployment Customer', value: '1' },
                    { label: 'Multiple Solutions Customer', value: '2' },
                    { label: 'Repeat Deployment Customer', value: '3' },
                    { label: 'Long Term Customer', value: '4' }
                  ]}
                />
              </FormField>
            
              <FormField label="Account Manager Email" constraintText="Required for email notifications">
                <Input
                  value={formData.accountManagerEmail}
                  onChange={({ detail }) =>
                    handleFormChange('accountManagerEmail', detail.value)
                  }
                  placeholder="account.manager@example.com"
                  type="email"
                />
              </FormField>

              <FormField label="Account Manager Slack Alias" constraintText="Required for Slack notifications">
                <Input
                  value={formData.accountManagerAlias}
                  onChange={({ detail }) =>
                    handleFormChange('accountManagerAlias', detail.value)
                  }
                  placeholder="slack_username"
                />
              </FormField>
            </SpaceBetween>
          </Form>
        </Container>
        
        {/* Simplified Action Buttons */}
        <Container header={<Header>Test Actions</Header>}>
          <SpaceBetween direction="vertical" size="m">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="primary"
                onClick={handleSubmit}
                loading={loading}
                disabled={!formData.accountManagerEmail}
              >
                Send Test Email
              </Button>
              <Button
                variant="primary"
                onClick={handleTestSlackNotification}
                loading={slackLoading}
                disabled={!formData.accountManagerAlias}
              >
                Send Test Slack Message
              </Button>
              <Button
                variant="normal"
                onClick={handleClear}
              >
                Clear
              </Button>
            </SpaceBetween>
            
            {/* Results section */}
            {result && (
              <Alert
                type={result.success ? 'success' : 'error'}
                header={result.success ? 'Email notification sent' : 'Failed to send email'}
              >
                {result.message}
              </Alert>
            )}
            
            {slackResult && (
              <Alert
                type={slackResult.success ? 'success' : 'error'}
                header={slackResult.success ? 'Slack notification sent' : 'Failed to send Slack notification'}
              >
                {slackResult.message}
              </Alert>
            )}
            
            {error && (
              <Alert
                type="error"
                header="Error"
                dismissible
              >
                {error.message}
              </Alert>
            )}
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </Box>
  );
};

export default EventTesting;
